/**
 * model/connectors.ts — Derivation header, stagger, positioning (powertrain).
 *
 * Layer `model/` (SPEC §6): contains the chain resolution for i
 * connectors (lock) as Object3D HIERARCHY (SPEC §6 point 7 — model a
 *mechanical arms). Each frame is relative to the parent; the constraints (alphaDeg,
 * stagger, notch) are joints that transform child links.
 *
 * Frame composition occurs via the NATIVE parent-child HIERARCHY of
 * three.js (position + quaternion locales; matrixWorld resolves the world):
 * - JOIST group = child of PIVOT alphaDeg (joist chain, G6)
 * - HEADER group = child of FramePlane ROOT (DOES NOT rotate with alphaDeg)
 * - `header frame = Rz(180°) ∘ frame joist` (G3 derivation at FRAME level)
 *
 * mesh/holes SEPARATION (FIX A): the quaternion of the groups is the ONLY one
 * authoring→viewer conversion (`holesQuat`) — HOLES and normals frames.
 * The component euler (`component_rotation_deg`) is `meshQuaternion`
 * (instances) and is applied ONLY to the GLB body in the render (components/):
 * the holes DO NOT receive the euler (previously they were mirrored: VERY SERIOUS bug).
 *
 * The CHAIN (SPEC §4.5, AC-8) for the joist+header pair is COMMENTED in
 * `placeConnectorGroup` (STEP 1–4); STEP 5/6 are in fasteners/resolveScene.
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

import * as THREE from "three";
import type { GlbFeatures, Vec3 } from "../data/types";
import { resolveConnectorPlacement } from "../transforms/connectorPlacement";
import { eulerDegToQuat } from "../transforms/vectors";
import { roundTo } from "../utils/math";
import type { ConnectorInstance } from "./types";

/**
 * SPEC G4 / AC-9: `stagger = |connector_assembly.height − connector_module.height|`
 * (e.g. 215 − 201.9 = **13.1 mm**). It is the vertical phase shift between the two halves
 * of the joint.
 *
 * Stagger CONSUMPTION (important): in v1 the stagger NO longer positions i
 * lock with ±stagger/2 around the h. From fix P2 (G5/C1 flush) the
 * vertical positioning is entirely delegated to the G5/C1 formula
 * (`transforms/connectorFlush.ts`): lock-joist has TOP flush with top
 * beam (a h) and the lock-header is below, with top a `h − stagger`. The value
 * `stagger` remains in the contract as METADATA (ConnectorInstance instances) e
 * reappears inside `resolveConnectorHeights` (topGap).
 *
 * NOTE (precision): feature data is in mm with 1 decimal (e.g. 201.9).
 * Floating-point subtraction produces 13.099999999999994; we round to
 * 1 decimal to comply with the numerical criterion of the PLAN (`=== 13.1`) and to
 * a stable display in the configurator. TODO: if real data
 * will have more precision, review this rounding.
 *
 * @param features GlbFeatures of the connector (SSOT geometry).
 * @returns Stagger in mm (always ≥ 0, rounded to 1 decimal).*/
export function computeStagger(features: GlbFeatures): number {
  const assemblyHeight = features.connector_assembly.height;
  const moduleHeight = features.connector_module.height;
  const raw = Math.abs(assemblyHeight - moduleHeight);
  return roundTo(raw, 1);
}

/**
 * SPEC G9 / AC-4: Z positions of centers for N side-by-side connectors.
 *
 * With `wi = connector_assembly.width` directly from features (✅ confirmed
 * user 2026-08-01, no recalculation from `module.width × count`):
 * c1 = −totalWidth/2 + w1/2
 * c2 = c1 + w1/2 + w2/2
 * generalizable for N. The result is centered on the center of the plane
 * connector (z=0) and the positions are relative to the CENTER of each connector.
 *
 * @param assemblyWidths Assembly widths along Z (one per connector).
 * @returns Array of Z offsets (one per connector, in the same order).*/
export function nConnectorsOffsetsZ(assemblyWidths: number[]): number[] {
  const widths = assemblyWidths.map((w) => (Number.isFinite(w) ? w : 0));
  const totalWidth = widths.reduce((acc, w) => acc + w, 0);

  const offsets: number[] = [];
  let prevCenter = 0;
  widths.forEach((w, i) => {
    if (i === 0) {
      //c1 = −totalW/2 + w1/2 → first connector centered on the left.
      prevCenter = -totalWidth / 2 + w / 2;
    } else {
      //ci = c(i−1) + w(i−1)/2 + wi/2 → rests the center on the common edge.
      const prevWidth = widths[i - 1] ?? 0;
      prevCenter = prevCenter + prevWidth / 2 + w / 2;
    }
    offsets.push(prevCenter);
  });
  return offsets;
}

/**
 * Input of `placeConnectorGroup` (PLAN §2.13, adapted to kinematic chain).
 *
 * ADAPTATION to PLAN: instead of `alphaDeg`/`alphaAxisOrigin`
 * (manual rotation of the joist chain) the groups receive the PARENTS of the
 * hierarchy: `joistParent` (pivot alphaDeg) and `headerParent` (root FramePlane).
 * `pivotOffsetY` = `joistTopY − planeHeight` (0 in v1 flush) to position i
 * joist children in the pivot frame. `nConnectors` is replaced by `zCenter`
 * (G9 pre-computed by resolveScene).*/
export interface ConnectorGroupInput {
  /** Features SSOT of the connector (joist side).*/
  features: GlbFeatures;
  /** Lock product code (mock for now; reserved for useAssets, T13).*/
  itemId: string | null;
  /** GLB lock URL blob; null → parametric fallback box (R6).*/
  glbUrl: string | null;
  /** Features of a wider lock replacing the HEADER half only (joist half keeps `features`); null = same lock. */
  headerFeatures?: GlbFeatures | null;
  /** GLB of `headerFeatures`; null → parametric fallback box. */
  headerGlbUrl?: string | null;
  /** 0-based index of the connector (c1=0, c2=1, …).*/
  index: number;
  /**Z position of the center of THIS connector (G9, pre-calculated).*/
  zCenter: number;
  /** Stagger (mm) calculated for these features (computeStagger).*/
  stagger: number;
  /** Parent of the JOIST chain: the alphaDeg pivot (child group of FramePlane).*/
  joistParent: THREE.Group;
  /** Parent of the HEADER chain: the FramePlane root.*/
  headerParent: THREE.Group;
  /** Y offset of the pivot relative to the root (mm) = joistTopY − planeHeight (0 in v1 flush).*/
  pivotOffsetY: number;
  joistNotchOffset: Vec3;
  headerNotchOffset: Vec3;
  /**
   * Traslazione verso il basso del connettore quando notch+angolo sono
   * entrambi attivi (01-10-2026, richiesta utente) — SOLO applicata qui
   * (passata a `resolveConnectorPlacement`), calcolata dal chiamante
   * (`resolveScene.ts`, stessa formula del backend). Default 0.*/
  joistNotchDownwardShiftMm?: number;
  /** Distance (mm) of the assembly's top below the beams' top face (form's
   * connector top distance); moves joist and header connectors rigidly down.
   * Default 0 (flush).*/
  connectorTopDistanceMm?: number;
}

/**
 * Place the joist+header pair of ONE connector in the hierarchy (PLAN §2.13).
 *
 * Le formule di posizionamento (conversione autoring→viewer, flush X/Y,
 * mirror G3 dell'header, correzione di orientamento del corpo) sono TUTTE
 * in `transforms/connectorPlacement.ts:resolveConnectorPlacement` (SSOT,
 * pura, testabile senza scene graph) — questa funzione fa SOLO da adattatore:
 * costruisce la gerarchia `THREE.Group` (notch offset, joist=figlio del
 * pivot alphaDeg/SPEC G6, header=figlio della root/SPEC G3) e applica i
 * valori gia' calcolati.
 *
 * `position`/`quaternion` sui `ConnectorInstance` restituiti sono
 * placeholder: `resolveScene` li compila dal frame world dopo
 * `updateMatrixWorld` (powertrain).
 *
 * @param input Positioning parameters (ConnectorGroupInput).
 * @returns Pair { joist, header } of ConnectorInstance (frame already attached).*/
export function placeConnectorGroup(input: ConnectorGroupInput): {
  joist: ConnectorInstance;
  header: ConnectorInstance;
} {
  const {
    features,
    glbUrl,
    headerFeatures = null,
    headerGlbUrl = null,
    index,
    zCenter,
    stagger,
    joistParent,
    headerParent,
    pivotOffsetY,
    joistNotchOffset,
    headerNotchOffset,
    joistNotchDownwardShiftMm = 0,
    connectorTopDistanceMm = 0,
  } = input;

  //Tutte le formule pure di posizionamento (flush X/Y, conversione autoring→
  //viewer, correzione di orientamento, mirror G3 per l'header) sono
  //consolidate in `transforms/connectorPlacement.ts` (SSOT unica, 25-09-2026
  //— prima erano composte inline qui, causa di piu' round di bug quando una
  //correzione veniva applicata in un punto ma non nell'altro consumatore).
  //Qui restano SOLO l'assemblaggio della gerarchia `THREE.Group` (notch
  //offset, parent chain) e l'applicazione dei valori gia' calcolati.
  // Both are downward translations of the whole assembly: they add up.
  const downwardShiftMm = joistNotchDownwardShiftMm + connectorTopDistanceMm;
  const { joist: joistPlacement, header: sameLockHeaderPlacement, flush } = resolveConnectorPlacement(
    features,
    zCenter,
    pivotOffsetY,
    downwardShiftMm,
  );
  // A wider header lock is placed from its OWN features, at the same pair center: its module position and
  // flush heights belong to that lock, not to the joist's.
  const headerSource = headerFeatures ?? features;
  const headerResolved = headerFeatures
    ? resolveConnectorPlacement(headerFeatures, zCenter, pivotOffsetY, downwardShiftMm)
    : null;
  const headerPlacement = headerResolved?.header ?? sameLockHeaderPlacement;
  const headerFlush = headerResolved?.flush ?? flush;

  //STEP 2 — JOIST: figlio del pivot alphaDeg (joist chain, SPEC G6) — cosi'
  //alphaDeg ruota automaticamente l'intera catena joist.
  const joistNotchGroup = new THREE.Group();
  joistNotchGroup.name = `connector-${index}-joist-notch-offset`;
  joistNotchGroup.position.set(...joistNotchOffset);
  joistParent.add(joistNotchGroup);
  const joistGroup = new THREE.Group();
  joistGroup.name = `connector-${index}-joist`;
  joistGroup.position.set(...joistPlacement.position);
  joistGroup.quaternion.copy(joistPlacement.quaternion);
  joistNotchGroup.add(joistGroup);

  //STEP 3 — HEADER: figlio della ROOT (NOT del pivot) — alphaDeg NON lo ruota
  //(SPEC G3/G6: solo il joist si inclina, l'header resta fisso al piano).
  const headerNotchGroup = new THREE.Group();
  headerNotchGroup.name = `connector-${index}-header-notch-offset`;
  headerNotchGroup.position.set(...headerNotchOffset);
  headerParent.add(headerNotchGroup);
  const headerGroup = new THREE.Group();
  headerGroup.name = `connector-${index}-header`;
  headerGroup.position.set(...headerPlacement.position);
  headerGroup.quaternion.copy(headerPlacement.quaternion);
  headerNotchGroup.add(headerGroup);

  //component euler — ONLY applied to GLB body (mesh), NEVER to holes (FIX A).
  const meshQuaternion = eulerDegToQuat(features.rotation.component_rotation_deg);

  //Note: `position`/`quaternion` are placeholders here; resolveScene them
  //compile from frame world AFTER updateMatrixWorld (powertrain).
  const joist: ConnectorInstance = {
    index,
    side: "joist",
    features,
    module: features.connector_module,
    assembly: features.connector_assembly,
    frame: joistGroup,
    position: [0, 0, 0],
    quaternion: new THREE.Quaternion(),
    meshQuaternion,
    stagger,
    offsetY: flush.joistCenterOffsetY,
    notchShift: [0, 0, 0],
    glbUrl,
  };

  const header: ConnectorInstance = {
    index,
    side: "header",
    features: headerSource,
    module: headerSource.connector_module,
    assembly: headerSource.connector_assembly,
    frame: headerGroup,
    position: [0, 0, 0],
    quaternion: new THREE.Quaternion(),
    meshQuaternion: headerFeatures ? eulerDegToQuat(headerFeatures.rotation.component_rotation_deg) : meshQuaternion,
    stagger: headerFeatures ? computeStagger(headerFeatures) : stagger,
    offsetY: headerFlush.headerCenterOffsetY,
    notchShift: [0, 0, 0],
    glbUrl: headerFeatures ? headerGlbUrl : glbUrl,
  };

  return { joist, header };
}

/**
 * Bounding sphere of the resolved connectors, for framing them in a capture.
 * Layer `model/`: the geometry stays here, the capture only consumes it.
 * Each instance is expanded by the half-diagonal of its assembly box, which is
 * orientation-independent — the joist side is rotated by alphaDeg, so an
 * axis-aligned box of the dims alone would clip it.
 *
 * @param connectors Resolved connector instances (may be empty).
 * @returns World center and radius (mm); radius 0 when there is nothing.*/
export function connectorsFocus(connectors: readonly ConnectorInstance[]): { center: Vec3; radius: number } {
  if (connectors.length === 0) return { center: [0, 0, 0], radius: 0 };

  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const instance of connectors) {
    const half =
      Math.hypot(instance.assembly.width, instance.assembly.height, instance.assembly.thickness) / 2;
    const [x, y, z] = instance.position;
    minX = Math.min(minX, x - half); maxX = Math.max(maxX, x + half);
    minY = Math.min(minY, y - half); maxY = Math.max(maxY, y + half);
    minZ = Math.min(minZ, z - half); maxZ = Math.max(maxZ, z + half);
  }

  const center: Vec3 = [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
  const radius = Math.hypot(maxX - center[0], maxY - center[1], maxZ - center[2]);
  return { center, radius };
}
